import {_} from 'lodash';
import {IObjectData, IOverride, ISyncField, ISystemPermission} from "../../index";
import {ObjectTypeId, Permission} from "../../constants";
import {_d, getEadmRole, getObjectDisplayName} from "../../util/misc";
import {convertToServerString} from "../../util/datetime";
import {global} from "../../appContext";
import {IMoreStatus} from "./index";
import {moment} from 'moment';
import {nsSyncFields} from "../../req/nsSyncFields";
import {ko} from "../../knockout-global";
import PromiseRequest from "../../req/PromiseRequest";

export namespace nsOverride {
    /** Deferred until global.init() — eager production bundle evaluates all modules before init. */
    export const showOverride: KnockoutComputed<boolean> = ko.pureComputed(
        () => !global.isReady() ? false : global.permissionValidator.isMinCompanyAdmin(),
    );

    export type TOverrideStatus = "r00" | "r10" | "r01" | "r11" | "a00" | "a10" | "a01" | "a11";

    export const EMPTY_OVERRIDE: IOverride = {
        comment: '',
        companyid: '',
        deletedby: '',
        deletedbyid: 0,
        deletedwhen: '',
        departmentid: 0,
        departmentname: '',
        expires: '',
        id: 0,
        objectid: 0,
        objectsrc: '',
        objecttypeid: 0,
        overriddenby: '',
        overriddenbyid: 0,
        overriddenwhen: '',
        parentsourceid: 0,
        parentsourcename: '',
        sourcename: '',
        syncronizedimportfileid: 0,
        targetname: '',
        targetobjectid: 0,
        targetobjectsrc: '',
        targetobjecttypeid: 0,
        value: '',
    };

    export const getBasicOverride = (companyid = global.login.selectedOrganizationId()): IOverride => {
        const user = global.login.user();
        return _.defaults({
            companyid: companyid,
            overriddenby: getObjectDisplayName(user ?? undefined),
            overriddenbyid: user?.id ?? 0,
            overriddenwhen: convertToServerString(moment()),
        }, EMPTY_OVERRIDE);
    };

    export enum OverrideValue {
        Add = 'A',
        Remove = 'R',
        /** Altinn retain — do not auto-revoke this grant on leaver. Value is `K` or `K`+legalUnitId. */
        Retain = 'K',
    }

    /** Purpose + sync (+ soft-delete) suffix for override status pills (membership / permissions). */
    export type TOverrideStatusSuffix =
        | 'add.pending'
        | 'add.synced'
        | 'add.delete.pending'
        | 'add.delete.synced'
        | 'remove.pending'
        | 'remove.synced'
        | 'remove.delete.pending'
        | 'remove.delete.synced'
        | 'retain.pending'
        | 'retain.synced'
        | 'retain.delete.pending'
        | 'retain.delete.synced';

    /** A/R/K override pill chrome via shared `.ds-status-pill` tones (excludes permissions-only `info` / Modified). */
    export type TOverrideArPillVariant = 'success' | 'danger' | 'info';

    const OVERRIDE_STATUS_PILL_VARIANT: Record<TOverrideStatusSuffix, TOverrideArPillVariant> = {
        'remove.pending': 'danger',
        'remove.synced': 'danger',
        'add.pending': 'success',
        'add.synced': 'success',
        'add.delete.pending': 'danger',
        'remove.delete.pending': 'success',
        'add.delete.synced': 'danger',
        'remove.delete.synced': 'danger',
        'retain.pending': 'info',
        'retain.synced': 'info',
        'retain.delete.pending': 'danger',
        'retain.delete.synced': 'danger',
    };

    /**
     * Shared `add|remove|retain[|.delete].{pending|synced}` key fragment from override value + sync + soft-delete.
     * Active retain (`K`) is always `retain.synced`: Altinn honors it immediately; user sync only
     * stamps SyncronizedImportFileId. Soft-deleted retain still uses pending/synced for clear-cancel.
     */
    export function getOverrideStatusSuffix(override: IOverride): TOverrideStatusSuffix {
        const letter = override.value?.charAt(0);
        const deleted = !!override.deletedbyid;
        const purpose = letter === OverrideValue.Add
            ? 'add'
            : letter === OverrideValue.Retain
                ? 'retain'
                : 'remove';
        if (purpose === 'retain' && !deleted) {
            return 'retain.synced';
        }
        const state = !!override.syncronizedimportfileid ? 'synced' : 'pending';
        return (deleted ? `${purpose}.delete.${state}` : `${purpose}.${state}`) as TOverrideStatusSuffix;
    }

    /** Pill chrome for an A/R/K override (same mapping for membership and permissions). */
    export function getOverrideStatusPillVariant(override: IOverride): TOverrideArPillVariant {
        return OVERRIDE_STATUS_PILL_VARIANT[getOverrideStatusSuffix(override)] ?? 'danger';
    }

    /**
     * Effective remove for an A/R override: active remove, or a soft-deleted add.
     * Permissions and membership rows use this for the expanded danger accent.
     * Retain (`K`) never marks the permission as removed.
     */
    export function isOverrideEffectivelyRemoved(override: IOverride | null | undefined): boolean {
        if (!override?.value) {
            return false;
        }
        if (override.value.startsWith(OverrideValue.Retain)) {
            return false;
        }
        if (override.value.startsWith(OverrideValue.Remove)) {
            return !override.deletedbyid;
        }
        if (override.value.startsWith(OverrideValue.Add)) {
            return !!override.deletedbyid;
        }
        return false;
    }

    /**
     * Soft-deleted override awaiting sync (`*.delete.pending`).
     * Matches BE restoreoverride: Deleted set and SyncronizedImportFileId cleared.
     */
    export function isOverridePendingDeletion(override: IOverride | null | undefined): boolean {
        return !!override?.deletedbyid && !override.syncronizedimportfileid;
    }

    /**
     * Confirm kind when cancelling a soft-delete awaiting sync.
     * `add|retain.delete.pending` → cancel pending delete (keep grant / keep retain);
     * `remove.delete.pending` → cancel pending restore.
     */
    export type TCancelPendingDeletionConfirmKind = 'delete' | 'restore';

    export function getCancelPendingDeletionConfirmKind(
        override: IOverride | null | undefined,
    ): TCancelPendingDeletionConfirmKind | null {
        if (!isOverridePendingDeletion(override)) {
            return null;
        }
        return override!.value?.charAt(0) === OverrideValue.Remove ? 'restore' : 'delete';
    }

    /** Undo soft-delete via `restoreoverride` (`*.delete.pending` → active again). */
    export function requestRestoreOverride(
        req: PromiseRequest,
        override: IOverride,
    ): Promise<unknown> {
        if (!override?.id) {
            return Promise.reject('missing override');
        }
        const body: {id: number; orgnr?: string} = {id: override.id};
        if (override.companyid) {
            body.orgnr = override.companyid;
        }
        return req.request(body);
    }

    export const getSyncFieldOverride = (object: IObjectData, sf: ISyncField, activeOnly = false): IOverride | null => {
        let overrides = object.overrides;
        if (!overrides) {
            return null;
        }
        return overrides
            .filter((o => {
                const a = o.objecttypeid == object.type.id;
                const b = o.targetobjectid == sf.syncfieldid;
                const c = !activeOnly || !o.deletedbyid;
                return a && b && c;
            }))
            .sort((a, b) => b.id - a.id)[0] ?? null;
    }

    const TitleTypes: string[] = [];
    TitleTypes[ObjectTypeId.Group] = 'group membership';
    TitleTypes[ObjectTypeId.System] = 'permission';

    export const getMoreStatusByOverride = (override: IOverride, objectTypeId: ObjectTypeId): IMoreStatus => {
        const synced = !!override.syncronizedimportfileid;
        const deleted = !!override.deletedbyid;
        const overrideValue = override.value?.charAt(0) as OverrideValue;
        return getMoreStatus(synced, deleted, overrideValue, objectTypeId);
    }

    export const getMoreStatus = (synced: boolean, deleted: boolean, overrideValue: string, objectTypeId: ObjectTypeId): IMoreStatus => {
        const type = TitleTypes[objectTypeId];

        let title = 'status.' + {
            "r00": 'will remove',
            "r10": 'is removed',
            "r01": 'will restore',
            "r11": 'is restored',
            "a00": 'will add',
            "a10": 'is added',
            "a01": 'will remove',
            "a11": 'is removed',
        }[getStatus(synced, deleted, overrideValue)]

        const synchronized = `status.${synced ? 'is': 'is not'} synchronized`

        let what = ''
        // r = remove (automatically assigned) / a = add (manually assigned)
        if (overrideValue.toLowerCase().startsWith('a')) {
            what = `status.manually assigned`
        }
        if (overrideValue.toLowerCase().startsWith('r')) {
            what = `status.automatically assigned`
        }
        what += `|type:((${type}|ac))`

        return {title, synchronized, what}
    }

    export const getStatus = (synced: boolean, deleted: boolean, overrideValue: string): TOverrideStatus => {
        let ret = overrideValue.charAt(0).toLowerCase();
        ret += Number(Boolean(synced));
        ret += Number(Boolean(deleted));
        switch (ret) {
            case 'r11':
            case 'a11':
                _d.warn(`impossible combo ${ret}`);
        }
        return ret as TOverrideStatus;
    }

    export const getStatusByOverride = (override: IOverride): TOverrideStatus => {
        return getStatus(!!override.syncronizedimportfileid, !!override.deletedbyid, override.value);
    }

    export const isSystemPermissionOverridePair = (systemPermission: ISystemPermission, override: IOverride): boolean => {
        const a = override?.companyid == systemPermission?.companyid;
        const b = override.objecttypeid == ObjectTypeId.System;
        const c = override?.parentsourceid == systemPermission?.system?.id;
        const d = override?.objectid == systemPermission?.role?.id;
        const e = (override?.departmentid ?? 0) == (systemPermission?.managerOf ?? 0);
        return a && b && c && d && e;
    }

    export function isSyncfieldOverrideable(syncfield: ISyncField): boolean {
        const permissionRequirement = syncfield.isoverridablebylevel || Permission.CompanyAdmin;
        return nsSyncFields.isSyncfieldMutable(syncfield) && getEadmRole() >= permissionRequirement
    }

    /** Syncfield override pill: pending / synced / pending restore (not A/R). */
    export interface ISyncfieldOverrideDisplay {
        translationKey: string;
        pillVariant: 'success' | 'info' | 'danger';
    }

    /** Status pill params for source-data (syncfield) overrides. */
    export function getSyncfieldOverrideDisplay(override: IOverride): ISyncfieldOverrideDisplay {
        // Soft-delete awaiting sync — restoreoverride still applies.
        if (isOverridePendingDeletion(override)) {
            return {translationKey: 'view.objectdetail.sourcedata.override.delete.pending', pillVariant: 'info'};
        }
        // Soft-delete already stamped by sync — cancel-pending is no longer valid.
        if (override.deletedbyid) {
            return {translationKey: 'view.objectdetail.sourcedata.override.delete.synced', pillVariant: 'danger'};
        }
        if (override.syncronizedimportfileid) {
            return {translationKey: 'view.objectdetail.sourcedata.override.synced', pillVariant: 'success'};
        }
        return {translationKey: 'view.objectdetail.sourcedata.override.pending', pillVariant: 'info'};
    }

}